import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserId, getUserRole } from 'utils/sessionStorage';

/** The signed-in person. Student screens act on their own student id. */
export function useEpUser() {
  const id = Number(getUserId() ?? 0);
  const role = (getUserRole() || sessionStorage.getItem('userRole') || 'student').toLowerCase();
  return { userId: id, role, isStudent: role === 'student' };
}

/** Loads data and keeps loading/error state. reload() fetches again. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);
  const run = useCallback(() => {
    const n = ++seq.current;
    setLoading(true);
    return fn()
      .then((d) => {
        if (n === seq.current) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => n === seq.current && setError(e))
      .finally(() => n === seq.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    run();
  }, [run]);
  return { data, setData, loading, error, reload: run };
}

/** Per-person convenience setting kept in this browser (filters). Never required. */
export function useLocalState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? { ...initial, ...JSON.parse(raw) } : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T) => {
      setV(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage blocked: keep it in memory */
      }
    },
    [key],
  );
  return [v, set];
}
